import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { CatalogPicker } from "./CatalogPicker";
import { ConnectorManagerModal } from "./connectors/ConnectorManagerModal";
import { DashboardRenderer } from "./DashboardRenderer";
import { Flowchart } from "./Flowchart";
import { StepEditor } from "./StepEditor";
import { catalogEntries, goldenVersion } from "../lib/fixtures";
import { fetchCatalogIndex } from "../lib/catalogIndex";
import { useBuilderStore, type BuilderState } from "../store";
import { builderHolderId } from "../lib/lease";
import { applyBuilderProposal, type BuilderDraftSnapshot } from "../lib/builderProposals";
import {
  ApiError,
  acquireBuilderLease,
  createBuilderThread,
  createProbeSession,
  createTask,
  finalizeDraft,
  getBuilderThread,
  getTemplate,
  launchRun,
  listBuilderMessages,
  loadBubbles,
  loadComponentCandidates,
  loadCoverage,
  releaseBuilderLease,
  runPreflight,
  saveDraft,
  streamBuilderTurn,
  type ProbeSession,
} from "../api";
import type { ComponentCandidate, CoverageReport, PreflightPlan } from "../lib/types";

const STEPS = [
  "Definition",
  "Name & description",
  "Connectors",
  "Building blocks",
  "Clarify",
  "Flowchart",
  "Dashboard",
  "Save & run",
] as const;

function snapshotOf(state: BuilderState): BuilderDraftSnapshot {
  return {
    name: state.name,
    description: state.description,
    plan: state.plan,
    dashboard: state.dashboard,
    flowchart: state.flowchart,
    selectedConnectors: state.selectedConnectors,
  };
}

export function BuilderWizard() {
  const params = useParams({ from: "/templates/$templateId/builder" });
  const templateId = params.templateId;
  const navigate = useNavigate();
  const state = useBuilderStore();
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([
    { role: "assistant", content: "What should this workflow do?" },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ComponentCandidate[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [preflight, setPreflight] = useState<PreflightPlan | null>(null);
  const [coverage, setCoverage] = useState<CoverageReport | null>(null);
  const [probe, setProbe] = useState<ProbeSession | null>(null);
  const [leaseHeldBy, setLeaseHeldBy] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const holder = useRef(builderHolderId()).current;
  const threadRef = useRef<string | null>(null);
  const readOnly = Boolean(leaseHeldBy);
  // Start from the bundled seed so first paint never waits on the network; replace it with
  // the KV-backed index once the conditional request resolves.
  const [entries, setEntries] = useState(catalogEntries);
  const [managing, setManaging] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCatalogIndex()
      .then((next) => {
        if (!cancelled) setEntries(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadBubbles(state.accountId).then(state.setBubbles);
  }, [state.accountId, state.setBubbles]);

  // Load the real template/draft, resume (or create) the builder thread, and replay history.
  // A failure keeps the read-only golden-fixture preview so the builder still renders offline.
  useEffect(() => {
    let cancelled = false;
    const store = useBuilderStore.getState();
    store.setTemplateId(templateId);
    (async () => {
      try {
        const detail = await getTemplate(templateId, store.accountId);
        if (cancelled) return;
        const draft = detail.draft;
        store.hydrate({
          name: draft.name,
          description: draft.description,
          plan: draft.plan,
          dashboard: draft.dashboard_schema,
          flowchart: draft.flowchart_view,
          connectors: draft.connectors,
          draftId: draft.draft_id,
          revision: draft.optimistic_revision,
        });
        store.setVersionId(detail.current_version?.version_id);

        const storageKey = `taskjinn.builder.thread.${draft.draft_id}`;
        let thread = null;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          try {
            thread = await getBuilderThread(stored, store.accountId);
          } catch {
            thread = null;
          }
        }
        if (!thread) {
          thread = await createBuilderThread({
            accountId: store.accountId,
            templateId,
            draftId: draft.draft_id,
          });
          localStorage.setItem(storageKey, thread.thread_id);
        }
        if (cancelled) return;
        setThreadId(thread.thread_id);
        threadRef.current = thread.thread_id;
        store.setThread(thread.thread_id);
        store.setServiceAvailable(true);

        // One active writer per draft. A second device goes read-only until "resume here?".
        try {
          const lease = await acquireBuilderLease({
            threadId: thread.thread_id,
            accountId: store.accountId,
            holder,
          });
          setLeaseHeldBy(lease.status === "held" ? lease.holder ?? "another device" : null);
        } catch {
          // An older/absent lease endpoint must not make the builder unusable; the turn itself
          // still renews the lease server-side.
          setLeaseHeldBy(null);
        }

        const history = await listBuilderMessages(thread.thread_id, store.accountId);
        const visible = history
          .filter((message) => message.role !== "tool")
          .map((message) => ({
            role: message.role as "user" | "assistant",
            content: message.content,
          }));
        if (!cancelled && visible.length > 0) setMessages(visible);
      } catch (error) {
        if (cancelled) return;
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          templateId,
        );
        if (isUuid && error instanceof ApiError && (error.status === 403 || error.status === 404)) {
          // A real draft link for another tenant/deleted template must never fall back to the
          // demo fixture: render "no access".
          setAccessDenied(true);
        }
        store.setServiceAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
      if (threadRef.current) {
        void releaseBuilderLease({
          threadId: threadRef.current,
          accountId: store.accountId,
          holder,
        });
      }
    };
  }, [templateId, holder]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const appendToken = (token: string) => {
    setMessages((current) => {
      const next = [...current];
      const last = next[next.length - 1];
      if (last?.role === "assistant") {
        next[next.length - 1] = { ...last, content: last.content + token };
      } else {
        next.push({ role: "assistant", content: token });
      }
      return next;
    });
  };

  const appendAssistant = (text: string) => {
    setMessages((current) => {
      const next = [...current];
      const last = next[next.length - 1];
      if (last?.role === "assistant" && last.content.trim() === "") {
        next[next.length - 1] = { ...last, content: text };
      } else if (text) {
        next.push({ role: "assistant", content: text });
      }
      return next;
    });
  };

  const draftPayload = () => {
    const current = useBuilderStore.getState();
    return {
      draft_id: current.draftId ?? "",
      template_id: templateId,
      account_id: current.accountId,
      name: current.name,
      description: current.description,
      plan: current.plan,
      dashboard_schema: current.dashboard,
      flowchart_view: current.flowchart,
      status: "in_progress",
      incomplete_sections: [],
      connectors: current.selectedConnectors,
      optimistic_revision: current.draftRevision,
      updated_at: new Date().toISOString(),
    };
  };

  const send = async () => {
    const content = input.trim();
    if (!content || streaming) return;
    if (!threadId) {
      state.setStatus(
        "The builder service is unreachable, so this is a read-only preview. Reload when the API is available.",
      );
      return;
    }
    setInput("");
    setMessages((current) => [
      ...current,
      { role: "user", content },
      { role: "assistant", content: "" },
    ]);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamBuilderTurn({
        threadId,
        accountId: state.accountId,
        message: content,
        draft: draftPayload(),
        holder,
        signal: controller.signal,
        onEvent: (event) => {
          switch (event.type) {
            case "token":
              if (event.kind !== "reasoning" && event.content) appendToken(event.content);
              break;
            case "proposal": {
              const current = snapshotOf(useBuilderStore.getState());
              const result = applyBuilderProposal(current, String(event.kind), event.payload);
              if (result.changed) useBuilderStore.getState().applySnapshot(result.snapshot);
              if (result.status) state.setStatus(result.status);
              break;
            }
            case "clarification":
              if (event.question) appendAssistant(event.question);
              break;
            case "tool":
              if (!event.accepted) {
                state.setStatus(`${event.name ?? "tool"} was rejected: ${event.error ?? "invalid"}`);
              }
              break;
            case "error":
              state.setStatus(event.message ?? "The builder hit an error.");
              if (event.message) appendAssistant(event.message);
              break;
            default:
              break;
          }
        },
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Another tab took the pen. Refresh the holder and drop to read-only.
        setLeaseHeldBy((current) => current ?? "another device");
        if (threadId) {
          try {
            const lease = await acquireBuilderLease({
              threadId,
              accountId: state.accountId,
              holder,
            });
            setLeaseHeldBy(lease.status === "held" ? lease.holder ?? "another device" : null);
          } catch {
            // Keep the generic held banner.
          }
        }
        state.setStatus("Another device is editing this draft. Choose “Resume here” to continue.");
        return;
      }
      const message = error instanceof Error ? error.message : "The builder is unavailable right now.";
      state.setStatus(message);
      appendAssistant("The builder is unavailable right now. Your draft is safe.");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const resumeHere = async () => {
    if (!threadId) return;
    try {
      const lease = await acquireBuilderLease({
        threadId,
        accountId: state.accountId,
        holder,
        force: true,
      });
      if (lease.status === "acquired") {
        setLeaseHeldBy(null);
        state.setStatus("You have the pen on this draft again.");
      } else {
        setLeaseHeldBy(lease.holder ?? "another device");
      }
    } catch (error) {
      state.setStatus(error instanceof Error ? error.message : "Could not take over the draft");
    }
  };

  const applyStepPatch = (plan: typeof state.plan, flowchart: typeof state.flowchart) => {
    const current = useBuilderStore.getState();
    current.applySnapshot({ ...snapshotOf(current), plan, flowchart });
    current.setStatus("Step updated in the draft.");
  };

  const save = async () => {
    if (readOnly) {
      state.setStatus("Read-only: another device is editing this draft. Choose “Resume here” first.");
      return;
    }
    if (!state.draftId) {
      state.setStatus("This is a read-only preview; there is no draft to save yet.");
      return;
    }
    try {
      const saved = await saveDraft({
        accountId: state.accountId,
        templateId,
        draftId: state.draftId,
        revision: state.draftRevision,
        name: state.name,
        description: state.description,
        plan: state.plan,
        dashboard: state.dashboard,
        flowchart: state.flowchart,
      });
      state.markSaved(saved.optimistic_revision);
    } catch (error) {
      state.setStatus(error instanceof Error ? error.message : "save failed");
    }
  };

  const finalize = async () => {
    if (!state.draftId) {
      state.setStatus("This is a read-only preview; there is no draft to finalize.");
      return;
    }
    try {
      const result = await finalizeDraft({
        accountId: state.accountId,
        templateId,
        draftId: state.draftId,
      });
      if (result.outcome === "change_request") {
        state.setStatus("Finalize sent to an Owner/Admin for approval.");
        return;
      }
      if (result.version?.version_id) state.setVersionId(result.version.version_id);
      state.setStatus("Version created. You can run it now.");
    } catch (error) {
      state.setStatus(error instanceof Error ? error.message : "finalize failed");
    }
  };

  const run = async () => {
    if (!state.versionId) {
      state.setStatus("Finalize the draft first — a run always executes a finalized version.");
      return;
    }
    try {
      state.setStatus("Creating the task…");
      const task = await createTask({
        accountId: state.accountId,
        templateId,
        name: state.name,
      });
      const created = await launchRun({ accountId: state.accountId, taskId: task.task_id });
      state.setStatus(`Run ${created.run_id.slice(0, 8)} started.`);
      await navigate({ to: "/tasks/$taskId", params: { taskId: task.task_id } });
    } catch (error) {
      state.setStatus(error instanceof Error ? error.message : "launch failed");
    }
  };

  const scanSite = async () => {
    if (!threadId) {
      state.setStatus("The builder service is unreachable, so a scan cannot start.");
      return;
    }
    const entry = entries.find((item) => item.entry_id === state.selectedConnectors[0]);
    const domain = entry?.match_domains?.[0];
    if (!domain) {
      state.setStatus("Select a connector or website with a known domain first.");
      return;
    }
    try {
      const session = await createProbeSession({
        threadId,
        accountId: state.accountId,
        approvedDomains: [domain],
      });
      setProbe(session);
      state.setStatus(
        `Read-only scan session opened for ${session.approved_domains.join(", ")} until ${new Date(
          session.expires_at,
        ).toLocaleTimeString()}.`,
      );
    } catch (error) {
      state.setStatus(error instanceof Error ? error.message : "scan failed");
    }
  };

  if (accessDenied) {
    return (
      <main className="tj-container max-w-xl py-10" data-testid="builder-access-denied">
        <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-6">
          <span className="tj-badge tj-badge-amber">No access</span>
          <h1 className="serif-heading mt-3 text-2xl">This draft isn’t available to your account</h1>
          <p className="mt-2 text-sm text-[var(--tj-muted)]">
            The template belongs to another workspace, was archived, or was deleted. Ask its owner
            to share it with your account.
          </p>
          <Link to="/dashboard" className="tj-btn tj-btn-primary mt-4 inline-block">
            Back to your dashboard
          </Link>
        </div>
      </main>
    );
  }

  const managingEntry = managing
    ? entries.find((entry) => entry.entry_id === managing)
    : undefined;

  return (
    <main className="tj-container space-y-4 py-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Interactive template builder</p>
          <h1 className="serif-heading text-2xl">{state.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {!state.serviceAvailable ? (
            <span className="tj-badge tj-badge-amber">Read-only preview (API unreachable)</span>
          ) : threadId ? (
            <span className="tj-badge tj-badge-green">Builder thread live</span>
          ) : null}
          {state.dirty ? <span className="tj-badge tj-badge-amber">Unsaved</span> : null}
          {state.statusMessage ? <span className="text-[var(--tj-muted)]">{state.statusMessage}</span> : null}
        </div>
      </header>

      {leaseHeldBy ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="lease-banner"
        >
          <span>
            Another device ({leaseHeldBy.slice(0, 12)}) is editing this draft. This tab is read-only
            until you take over.
          </span>
          <button type="button" className="tj-btn tj-btn-primary" onClick={() => void resumeHere()}>
            Resume here
          </button>
        </div>
      ) : null}

      <nav aria-label="Builder steps" className="flex gap-1 overflow-x-auto pb-1">
        {STEPS.map((label, index) => {
          const number = index + 1;
          return (
            <button
              key={label}
              type="button"
              onClick={() => state.setStep(number)}
              aria-current={state.currentStep === number ? "step" : undefined}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${
                state.currentStep === number
                  ? "bg-[var(--tj-accent)] text-white"
                  : "border border-[var(--tj-border)] bg-[var(--tj-surface)]"
              }`}
            >
              {number}. {label}
            </button>
          );
        })}
      </nav>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="space-y-4">
          {state.currentStep === 1 ? (
            <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-3">
              <div className="max-h-[420px] space-y-2 overflow-y-auto" aria-live="polite">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`rounded-lg px-3 py-2 text-sm ${
                      message.role === "user" ? "ml-auto bg-[var(--tj-accent)] text-white" : "bg-[var(--tj-bg)]"
                    } max-w-[90%]`}
                  >
                    {message.content}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  aria-label="Message the builder"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void send();
                  }}
                  placeholder="Describe the workflow in plain language"
                  disabled={readOnly}
                  className="min-w-0 flex-1 rounded-lg border border-[var(--tj-border)] px-3 py-2 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => (streaming ? abortRef.current?.abort() : void send())}
                  disabled={readOnly}
                  className="tj-btn tj-btn-primary"
                >
                  {streaming ? "Stop" : "Send"}
                </button>
              </div>
            </div>
          ) : null}

          {state.currentStep === 2 ? (
            <div className="space-y-3 rounded-xl border border-[var(--tj-border)] p-3">
              <label className="block text-sm">
                Name
                <input
                  value={state.name}
                  onChange={(event) => state.setName(event.target.value)}
                  className="mt-1 w-full rounded border border-[var(--tj-border)] px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                Description
                <textarea
                  value={state.description}
                  onChange={(event) => state.setDescription(event.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded border border-[var(--tj-border)] px-3 py-2"
                />
              </label>
            </div>
          ) : null}

          {state.currentStep === 3 ? (
            <CatalogPicker
              entries={entries}
              bubbles={state.bubbles}
              selected={state.selectedConnectors}
              onSelect={state.toggleConnector}
              onRequest={(entryId) => state.setStatus(`Requested ${entryId} — we'll notify you`)}
              onManage={setManaging}
            />
          ) : null}

          {state.currentStep === 4 ? (
            <div className="space-y-3 rounded-xl border border-[var(--tj-border)] p-3 text-sm">
              <p>Tenant sub-templates are discovered here, with global components matched by domain or AX signature. Matching proposes candidates; nothing is inserted automatically.</p>
              <button
                type="button"
                className="tj-btn tj-btn-secondary"
                onClick={async () => {
                  const entry = entries.find((item) => item.entry_id === state.selectedConnectors[0]);
                  const domain = entry?.match_domains[0];
                  try {
                    const found = await loadComponentCandidates(state.accountId, domain);
                    setCandidates(found);
                    state.setStatus(
                      found.length
                        ? `${found.length} component candidate(s) found — accept the ones you want.`
                        : "No library match. Scanning the site requires an explicit probe session.",
                    );
                  } catch {
                    state.setStatus("Component discovery is unavailable right now.");
                  }
                }}
              >
                Match library
              </button>
              <button
                type="button"
                className="tj-btn tj-btn-secondary ml-2"
                disabled={readOnly}
                onClick={() => void scanSite()}
              >
                Scan this site (read-only)
              </button>
              {probe ? (
                <p className="text-xs text-[var(--tj-muted)]" data-testid="probe-session">
                  Probe session for {probe.approved_domains.join(", ")} · expires{" "}
                  {new Date(probe.expires_at).toLocaleTimeString()}. Read-only: no submit, upload or
                  download.
                </p>
              ) : (
                <p className="text-xs text-[var(--tj-muted)]">
                  Scanning is explicit: it opens a time-boxed, read-only envelope for the selected
                  domain and never runs on its own.
                </p>
              )}
              <ul className="space-y-2">
                {candidates.map((candidate) => (
                  <li key={`${candidate.component_id}@${candidate.version}`} className="flex items-start justify-between gap-2 rounded border border-[var(--tj-border)] p-2">
                    <div>
                      <p className="font-medium">{candidate.name}</p>
                      <p className="text-xs text-[var(--tj-muted)]">
                        {candidate.reason} · {candidate.domains.join(", ") || "no domains"}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded border border-[var(--tj-border)] px-2 py-1 text-xs"
                      onClick={() =>
                        setSelectedComponents((current) =>
                          current.includes(candidate.component_id)
                            ? current.filter((id) => id !== candidate.component_id)
                            : [...current, candidate.component_id],
                        )
                      }
                    >
                      {selectedComponents.includes(candidate.component_id) ? "Accepted" : "Accept"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {state.currentStep === 5 ? (
            <div className="space-y-2 rounded-xl border border-[var(--tj-border)] p-3 text-sm">
              <p>Does this flow branch by document type, conditional question or outcome?</p>
              <p className="text-[var(--tj-muted)]">The plan already models w2 vs 1099 with an escalate path for unknown input.</p>
            </div>
          ) : null}

          {state.currentStep === 6 ? (
            <div className="space-y-3">
              <Flowchart plan={state.plan} hints={state.flowchart.nodes} />
              <StepEditor
                plan={state.plan}
                flowchart={state.flowchart.nodes}
                disabled={readOnly}
                onApply={(plan, flowchart) => applyStepPatch(plan, { nodes: flowchart })}
              />
              <div className="rounded-xl border border-[var(--tj-border)] p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">Coverage</p>
                  <button
                    type="button"
                    className="rounded border border-[var(--tj-border)] px-2 py-1 text-xs"
                    onClick={async () => setCoverage(await loadCoverage(state.accountId, state.templateId))}
                  >
                    Load coverage
                  </button>
                </div>
                {coverage ? (
                  <p className="mt-2 text-[var(--tj-muted)]">
                    Declared {coverage.declared_steps} steps, {coverage.declared_branches} branch
                    point(s), {coverage.declared_verification_paths} verification path(s). Observed{" "}
                    {coverage.observed_paths.length} path(s) in recent runs.
                  </p>
                ) : (
                  <p className="mt-2 text-[var(--tj-muted)]">
                    Declared paths are derived from the plan; observed paths come from recent run evidence.
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {state.currentStep === 7 ? (
            <div className="rounded-xl border border-[var(--tj-border)] p-3">
              <DashboardRenderer
                schema={state.dashboard}
                outputs={{ total: 1234.56, rows: [{ name: "receipt.pdf" }] }}
                verificationResults={[
                  {
                    run_id: "preview",
                    path_id: "preview",
                    test_id: "total_present",
                    assertion_kind: "output_present",
                    outcome: "fail",
                  },
                ]}
                allowedHosts={goldenVersion.template_domains.map((domain) => domain.domain)}
              />
            </div>
          ) : null}

          {state.currentStep === 8 ? (
            <div className="space-y-3 rounded-xl border border-[var(--tj-border)] p-3">
              <p className="text-sm">
                Save an incomplete draft at any time. Run requires a finalized version.
              </p>
              <p className="text-xs text-[var(--tj-muted)]" data-testid="version-status">
                {state.versionId
                  ? `Finalized version ${state.versionId.slice(0, 8)} is ready to run.`
                  : "No finalized version yet."}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="tj-btn tj-btn-secondary"
                  disabled={readOnly}
                  onClick={() => void save()}
                >
                  Save draft
                </button>
                <button
                  type="button"
                  className="tj-btn tj-btn-primary"
                  disabled={state.role === "run_only" || readOnly}
                  onClick={() => void finalize()}
                  title={state.role === "run_only" ? "Owner/Admin approval required" : undefined}
                >
                  Finalize
                </button>
              </div>
              {state.role === "run_only" ? (
                <p className="text-xs text-[var(--tj-muted)]">
                  You can propose finalize; an Owner or Admin must approve it.
                </p>
              ) : null}
              <button
                type="button"
                className="tj-btn tj-btn-primary"
                disabled={!state.versionId || readOnly}
                onClick={() => void run()}
                title={state.versionId ? undefined : "Finalize first"}
                data-testid="run-template"
              >
                Run this template
              </button>
              <button
                type="button"
                className="tj-btn tj-btn-secondary"
                disabled={readOnly}
                onClick={async () => {
                  try {
                    if (!state.versionId) {
                      state.setStatus("Finalize the draft before running a pre-flight.");
                      return;
                    }
                    const plan = await runPreflight({
                      accountId: state.accountId,
                      templateId,
                      versionId: state.versionId,
                      inputs: { document_type: "w2" },
                    });
                    setPreflight(plan);
                  } catch (error) {
                    state.setStatus(error instanceof Error ? error.message : "pre-flight failed");
                  }
                }}
              >
                Run pre-flight
              </button>
              {preflight ? (
                <div className="rounded-lg border border-[var(--tj-border)] p-3 text-sm">
                  <p className="font-medium">Pre-flight: {preflight.verdict.replace("_", " ")}</p>
                  <p className="text-[var(--tj-muted)]">{preflight.narrative}</p>
                  {preflight.missing_prereqs.length ? (
                    <ul className="mt-2 list-disc pl-5 text-red-700">
                      {preflight.missing_prereqs.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {preflight.unmodelled_input_dims.length ? (
                    <p className="mt-2 text-amber-700">
                      Unmodelled inputs: {preflight.unmodelled_input_dims.join(", ")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <aside className="space-y-3">
          <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-3">
            <h2 className="serif-heading mb-2 text-lg">Flow</h2>
            <ol className="space-y-1 text-sm">
              {state.plan.steps.map((step) => (
                <li key={step.step_id} className="flex items-center justify-between gap-2 rounded border border-[var(--tj-border)] px-2 py-1">
                  <span className="truncate">{state.flowchart.nodes.find((hint) => hint.step_id === step.step_id)?.label ?? step.kind}</span>
                  <span className="text-xs text-[var(--tj-muted)]">{step.kind}</span>
                </li>
              ))}
            </ol>
          </section>
          <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-3 text-sm">
            <h2 className="serif-heading mb-2 text-lg">Selected connectors</h2>
            {state.selectedConnectors.length === 0 ? (
              <p className="text-[var(--tj-muted)]">None yet.</p>
            ) : (
              <ul className="space-y-1">
                {state.selectedConnectors.map((entryId) => {
                  const entry = entries.find((item) => item.entry_id === entryId);
                  return <li key={entryId}>{entry?.display_name ?? entryId}</li>;
                })}
              </ul>
            )}
          </section>
        </aside>
      </div>
      {managingEntry ? (
        <ConnectorManagerModal
          entry={managingEntry}
          accountId={state.accountId}
          bubble={state.bubbles.find((bubble) => bubble.entry_id === managingEntry.entry_id)}
          onClose={() => setManaging(null)}
          onChanged={() => {
            void loadBubbles(state.accountId).then(state.setBubbles);
          }}
        />
      ) : null}
    </main>
  );
}
